import { useState, useMemo } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import { ChevronDown, ChevronUp } from "lucide-react";
import HoverTitle from "../HoverTitle";

const CategoryTable = ({ categories, onEdit, onDelete }) => {
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [expanded, setExpanded] = useState(null);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const getParentTitle = (parentId) => {
    const parent = categories.find((cat) => cat._id === parentId);
    return parent ? parent.name : "-";
  };

  const filteredCategories = useMemo(() => {
    let result = [...categories];

    if (search) {
      result = result.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase())
      );
    }

    result.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal < bVal) return sortAsc ? -1 : 1;
      if (aVal > bVal) return sortAsc ? 1 : -1;
      return 0;
    });

    return result;
  }, [categories, search, sortField, sortAsc]);

  const pagedCategories = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredCategories.slice(start, start + perPage);
  }, [filteredCategories, page, perPage]);

  const toggleExpand = (id) => {
    setExpanded((prev) => (prev === id ? null : id));
  };

  return (
    <div>
      <div className="d-flex gap-2 mb-3">
        <input
          className="form-control"
          placeholder="Search category..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="form-select"
          value={perPage}
          onChange={(e) => setPerPage(Number(e.target.value))}
        >
          {[5, 10, 20, 50].map((n) => (
            <option key={n} value={n}>
              {n} per page
            </option>
          ))}
        </select>
      </div>

      {/* Desktop Table */}
      <div className="d-none d-lg-block">
        <table className="table table-striped w-100 table-hover">
          <thead>
            <tr>
              <th>#</th>
              <th
                onClick={() => toggleSort("name")}
                style={{ cursor: "pointer" }}
              >
                Name
              </th>
              <th>Parent</th>
              <th>Slug</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pagedCategories.map((category, index) => (
              <tr key={category._id}>
                <td>{(page - 1) * perPage + index + 1}</td>
                <td> <HoverTitle
                      title={category.name}
                      imageUrl={`${category.image}`}
                    /></td>
                <td>{getParentTitle(category.parentId)}</td>
                <td>{category.slug}</td>
                <td>
                  <button
                    className="btn btn-sm btn-warning me-1"
                    onClick={() => onEdit(category)}
                  >
                    <FaEdit />
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => onDelete(category._id)}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            Showing{" "}
            {Math.min((page - 1) * perPage + 1, filteredCategories.length)} to{" "}
            {Math.min(page * perPage, filteredCategories.length)} of{" "}
            {filteredCategories.length} entries
          </div>
          <div className="btn-group">
            <button
              className="btn btn-secondary"
              disabled={page === 1}
              onClick={() => setPage((prev) => prev - 1)}
            >
              Previous
            </button>
            <button
              className="btn btn-secondary"
              disabled={page * perPage >= filteredCategories.length}
              onClick={() => setPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="d-lg-none">
        {pagedCategories.map((category) => (
          <div
            key={category._id}
            className="border rounded mb-3 shadow-sm overflow-hidden"
          >
            <div
              className="d-flex justify-content-between align-items-center p-2 bg-light"
              onClick={() => toggleExpand(category._id)}
              role="button"
            >
              <strong>{category.name}</strong>
              {expanded === category._id ? <ChevronUp /> : <ChevronDown />}
            </div>

            {expanded === category._id && (
              <div className="p-2 small bg-white">
                     <img src={`${category.image}`} class="img-fluid" alt={category.name} />
                <div>
                  <strong>Parent:</strong> {getParentTitle(category.parentId)}
                </div>
                <div>
                  <strong>Slug:</strong> {category.slug}
                </div>
                <div className="mt-2">
                  <button
                    onClick={() => onEdit(category)}
                    className="btn btn-sm btn-outline-primary me-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(category._id)}
                    className="btn btn-sm btn-outline-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        <div className="btn-group">
          <button
            className="btn btn-secondary"
            disabled={page === 1}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </button>
          <button
            className="btn btn-secondary"
            disabled={page * perPage >= filteredCategories.length}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </button>
        </div>
        <div>
          <div className="mt-2">
            Showing{" "}
            {Math.min((page - 1) * perPage + 1, filteredCategories.length)} to{" "}
            {Math.min(page * perPage, filteredCategories.length)} of{" "}
            {filteredCategories.length} entries
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryTable;

